import React, { useState, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Form layout types
export type FormLayout = 'vertical' | 'horizontal';

// Form validation types
export type FormValidationStatus = 'success' | 'error' | 'warning' | 'validating' | '';

// Form field validation rule
export interface FormValidationRule {
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Custom validation message
   */
  message?: string;
  /**
   * Custom validation function
   */
  validator?: (value: any) => boolean;
  /**
   * Minimum length for string values
   */
  minLength?: number;
  /**
   * Maximum length for string values
   */
  maxLength?: number;
  /**
   * Pattern for string validation
   */
  pattern?: RegExp;
}

// Form field
export interface FormField {
  /**
   * Field name (used as key)
   */
  name: string;
  /**
   * Field label
   */
  label?: string;
  /**
   * Field value
   */
  value: any;
  /**
   * Field validation rules
   */
  rules?: FormValidationRule[];
  /**
   * Field validation status
   */
  status?: FormValidationStatus;
  /**
   * Field error message
   */
  errorMessage?: string;
  /**
   * Field helper text
   */
  helperText?: string;
}

// Form props
export interface FormProps {
  /**
   * The layout of the form
   * @default 'vertical'
   */
  layout?: FormLayout;
  
  /**
   * Whether to show validation status icons
   * @default true
   */
  showValidationIcons?: boolean;
  
  /**
   * Whether to disable the form
   * @default false
   */
  disabled?: boolean;
  
  /**
   * The initial values of the form
   */
  initialValues?: Record<string, any>;
  
  /**
   * Callback when form values change
   */
  onChange?: (values: Record<string, any>) => void;
  
  /**
   * Callback when form is submitted
   */
  onSubmit?: (values: Record<string, any>) => void;
  
  /**
   * Callback when form validation fails
   */
  onValidationFailed?: (errors: Record<string, string>) => void;
  
  /**
   * The content of the form
   */
  children: React.ReactNode;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Form context
export interface FormContextProps {
  /**
   * The layout of the form
   */
  layout: FormLayout;
  
  /**
   * Whether to show validation status icons
   */
  showValidationIcons: boolean;
  
  /**
   * Whether the form is disabled
   */
  disabled: boolean;
  
  /**
   * The values of the form
   */
  values: Record<string, any>;
  
  /**
   * The errors of the form
   */
  errors: Record<string, string>;
  
  /**
   * The validation statuses of the form fields
   */
  validationStatuses: Record<string, FormValidationStatus>;
  
  /**
   * Set the value of a form field
   */
  setFieldValue: (name: string, value: any) => void;
  
  /**
   * Set the error of a form field
   */
  setFieldError: (name: string, error: string) => void;
  
  /**
   * Set the validation status of a form field
   */
  setFieldValidationStatus: (name: string, status: FormValidationStatus) => void;
  
  /**
   * Validate a form field
   */
  validateField: (name: string, value: any, rules?: FormValidationRule[]) => boolean;
  
  /**
   * Validate all form fields
   */
  validateForm: () => boolean;
  
  /**
   * Submit the form
   */
  submitForm: () => void;
}

// Create form context
export const FormContext = React.createContext<FormContextProps | undefined>(undefined);

// Form container styles based on layout
const getFormContainerStyles = (layout: FormLayout) => {
  switch (layout) {
    case 'horizontal':
      return `
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: ${spacingSystem.spacing[16]};
        align-items: center;
      `;
    case 'vertical':
    default:
      return `
        display: flex;
        flex-direction: column;
        gap: ${spacingSystem.spacing[16]};
      `;
  }
};

// Styled form component
const StyledForm = styled.form<{ layout: FormLayout }>`
  font-family: ${typography.fontFamily.primary};
  width: 100%;
  
  /* Layout styles */
  ${(props: { layout: FormLayout }) => getFormContainerStyles(props.layout)}
`;

/**
 * Form component for data collection with validation
 */
// Define a type that includes the Item property
interface FormComponent extends React.FC<FormProps> {
  Item: React.FC<FormItemProps>;
}

// Type guard for React elements
function isValidFormElement(child: React.ReactNode): child is React.ReactElement {
  return React.isValidElement(child);
}

export const Form: FormComponent = ({
  layout = 'vertical',
  showValidationIcons = true,
  disabled = false,
  initialValues = {},
  onChange,
  onSubmit,
  onValidationFailed,
  children,
  className,
  ...rest
}: FormProps) => {
  // Form state
  const [values, setValues] = useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationStatuses, setValidationStatuses] = useState<Record<string, FormValidationStatus>>({});
  
  // Set field value
  const setFieldValue = (name: string, value: any) => {
    const newValues = { ...values, [name]: value };
    setValues(newValues);
    
    // Call onChange callback
    if (onChange) {
      onChange(newValues);
    }
  };
  
  // Set field error
  const setFieldError = (name: string, error: string) => {
    setErrors((prev: Record<string, string>) => ({ ...prev, [name]: error }));
  };
  
  // Set field validation status
  const setFieldValidationStatus = (name: string, status: FormValidationStatus) => {
    setValidationStatuses((prev: Record<string, FormValidationStatus>) => ({ ...prev, [name]: status }));
  };
  
  // Validate field
  const validateField = (name: string, value: any, rules?: FormValidationRule[]): boolean => {
    // Clear previous error
    setFieldError(name, '');
    setFieldValidationStatus(name, '');
    
    // No rules, field is valid
    if (!rules || rules.length === 0) {
      return true;
    }
    
    // Set validating status
    setFieldValidationStatus(name, 'validating');
    
    // Check each rule
    for (const rule of rules) {
      // Required rule
      if (rule.required && (value === undefined || value === null || value === '')) {
        const message = rule.message || 'This field is required';
        setFieldError(name, message);
        setFieldValidationStatus(name, 'error');
        return false;
      }
      
      // Skip other validations if value is empty and not required
      if (value === undefined || value === null || value === '') {
        continue;
      }
      
      // Min length rule
      if (rule.minLength !== undefined && typeof value === 'string' && value.length < rule.minLength) {
        const message = rule.message || `Minimum length is ${rule.minLength} characters`;
        setFieldError(name, message);
        setFieldValidationStatus(name, 'error');
        return false;
      }
      
      // Max length rule
      if (rule.maxLength !== undefined && typeof value === 'string' && value.length > rule.maxLength) {
        const message = rule.message || `Maximum length is ${rule.maxLength} characters`;
        setFieldError(name, message);
        setFieldValidationStatus(name, 'error');
        return false;
      }
      
      // Pattern rule
      if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
        const message = rule.message || 'Invalid format';
        setFieldError(name, message);
        setFieldValidationStatus(name, 'error');
        return false;
      }
      
      // Custom validator rule
      if (rule.validator && !rule.validator(value)) {
        const message = rule.message || 'Validation failed';
        setFieldError(name, message);
        setFieldValidationStatus(name, 'error');
        return false;
      }
    }
    
    // All rules passed
    setFieldValidationStatus(name, 'success');
    return true;
  };
  
  // Validate all form fields
  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors: Record<string, string> = {};
    
    // Get all form fields from children
    // This is a simplified approach - in a real implementation,
    // you would need to traverse the children to find all form fields
    
    // For now, we'll just validate the values we have
    Object.keys(values).forEach(name => {
      const value = values[name];
      // Here you would get the rules for this field
      // For now, we'll just check if the field has a value
      const isFieldValid = validateField(name, value, [{ required: true }]);
      if (!isFieldValid) {
        isValid = false;
        newErrors[name] = errors[name];
      }
    });
    
    // Call onValidationFailed callback if form is invalid
    if (!isValid && onValidationFailed) {
      onValidationFailed(newErrors);
    }
    
    return isValid;
  };
  
  // Submit form
  const submitForm = () => {
    // Validate form
    const isValid = validateForm();
    
    // Submit if valid
    if (isValid && onSubmit) {
      onSubmit(values);
    }
  };
  
  // Handle form submission
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    submitForm();
  };
  
  // Form context value
  const formContextValue: FormContextProps = {
    layout: layout as FormLayout,
    showValidationIcons,
    disabled,
    values,
    errors,
    validationStatuses,
    setFieldValue,
    setFieldError,
    setFieldValidationStatus,
    validateField,
    validateForm,
    submitForm,
  };
  
  return (
    <FormContext.Provider value={formContextValue}>
      <StyledForm
        layout={layout}
        className={className}
        onSubmit={handleSubmit}
        noValidate
        {...rest}
      >
        {children}
      </StyledForm>
    </FormContext.Provider>
  );
};

// Form Item props
export interface FormItemProps {
  /**
   * The name of the form item
   */
  name: string;
  
  /**
   * The label of the form item
   */
  label?: string;
  
  /**
   * The helper text of the form item
   */
  helperText?: string;
  
  /**
   * The validation rules of the form item
   */
  rules?: FormValidationRule[];
  
  /**
   * Whether the form item is required
   * @default false
   */
  required?: boolean;
  
  /**
   * The content of the form item
   */
  children: React.ReactNode;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Styled form item components
const FormItemContainer = styled.div<{ layout: FormLayout }>`
  display: flex;
  flex-direction: ${(props: { layout: FormLayout }) => props.layout === 'horizontal' ? 'row' : 'column'};
  gap: ${(props: { layout: FormLayout }) => props.layout === 'horizontal' ? spacingSystem.spacing[16] : spacingSystem.spacing[8]};
  width: 100%;
  
  ${(props: { layout: FormLayout }) => props.layout === 'horizontal' && `
    align-items: center;
    grid-column: span 2;
  `}
`;

const Label = styled.label<{ required?: boolean; error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${(props: { error?: boolean }) => props.error ? colors.semantic.error.default : colors.neutral[800]};
  margin-bottom: ${spacingSystem.spacing[4]};
  
  ${(props: { required?: boolean }) => props.required && `
    &::after {
      content: '*';
      color: ${colors.semantic.error.default};
      margin-left: ${spacingSystem.spacing[4]};
    }
  `}
`;

const HelperText = styled.p<{ error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  color: ${(props: { error?: boolean }) => props.error ? colors.semantic.error.default : colors.neutral[600]};
  margin-top: ${spacingSystem.spacing[4]};
  margin-bottom: 0;
`;

const FormItemContent = styled.div`
  flex: 1;
`;

/**
 * Form Item component for individual form fields
 */
export const FormItem: React.FC<FormItemProps> = ({
  name,
  label,
  helperText,
  rules = [],
  required = false,
  children,
  className,
}: FormItemProps) => {
  // Get form context
  const formContext = React.useContext(FormContext);
  
  if (!formContext) {
    throw new Error('FormItem must be used within a Form component');
  }
  
  const {
    layout,
    errors,
    validationStatuses,
    validateField,
    values,
  } = formContext;
  
  // Add required rule if required prop is true
  const allRules = required ? [{ required: true }, ...rules] : rules;
  
  // Get error and validation status
  const error = errors[name];
  const validationStatus = validationStatuses[name];
  const hasError = Boolean(error);
  
  // Handle field change
  const handleFieldChange = (value: any) => {
    formContext.setFieldValue(name, value);
    validateField(name, value, allRules);
  };
  
  // Clone child with additional props - using a more type-safe approach
  const childrenWithProps = React.Children.map(children, (child) => {
    if (isValidFormElement(child)) {
      // Type-safe approach to clone the element
      return React.cloneElement(child, {
        // Only pass specific props that we know are safe
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          // Handle the change event
          const value = e.target.value;
          handleFieldChange(value);
          
          // Call the original onChange if it exists
          if (typeof child.props.onChange === 'function') {
            child.props.onChange(e);
          }
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          // Call the original onBlur if it exists
          if (typeof child.props.onBlur === 'function') {
            child.props.onBlur(e);
          }
        },
        // Pass other props that are safe
        name,
        value: values[name],
        error: Boolean(error),
        errorMessage: error,
        disabled: formContext.disabled || Boolean(child.props.disabled),
      });
    }
    return child;
  });
  
  return (
    <FormItemContainer layout={layout} className={className}>
      {label && (
        <Label htmlFor={name} required={required} error={hasError}>
          {label}
        </Label>
      )}
      
      <FormItemContent>
        {childrenWithProps}
        
        {(helperText || error) && (
          <HelperText error={hasError}>
            {hasError ? error : helperText}
          </HelperText>
        )}
      </FormItemContent>
    </FormItemContainer>
  );
};

// Add FormItem as a subcomponent
Form.Item = FormItem;

export default Form;